import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import it from '../locales/it.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import ru from '../locales/ru.json';
import zh from '../locales/zh.json';

/* ----------------------------------------
 * Constants
 * ------------------------------------- */

const STORAGE_KEY = 'app_language';
const FALLBACK_LANGUAGE = 'en';

/* ----------------------------------------
 * Language detection
 * ------------------------------------- */

const detectInitialLanguage = (): string => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;

  return (
    navigator.languages?.[0] ??
    navigator.language ??
    FALLBACK_LANGUAGE
  ).split('-')[0];
};

/* ----------------------------------------
 * i18n init
 * ------------------------------------- */

i18n
  .use(LanguageDetector)
  .init({
    lng: detectInitialLanguage(),
    fallbackLng: FALLBACK_LANGUAGE,
    debug: import.meta.env.DEV,
    resources: {
      en: { translation: en },
      it: { translation: it },
      de: { translation: de },
      es: { translation: es },
      fr: { translation: fr },
      ru: { translation: ru },
      zh: { translation: zh }
    },
    interpolation: {
      escapeValue: false
    }
  });

/* ----------------------------------------
 * Types
 * ------------------------------------- */

type LocalizedMeta = Record<string, Record<string, string>>;

interface FirestoreLocalizedItem {
  meta?: LocalizedMeta;
  [key: string]: any;
}

/* ----------------------------------------
 * Internal helpers
 * ------------------------------------- */

const getCurrentLanguage = () => i18n.language || FALLBACK_LANGUAGE;

/* ----------------------------------------
 * Localization logic
 * ------------------------------------- */

export const localizeFirestoreItem = (
  item: FirestoreLocalizedItem | null | undefined,
  keys: string[],
  lang: string = getCurrentLanguage()
): string | null => {
  if (!item || keys.length === 0) return null;

  const languages = [
    lang,
    getCurrentLanguage(),
    FALLBACK_LANGUAGE,
  ];

  for (const key of keys) {
    for (const l of languages) {
      const value = item.meta?.[l]?.[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    const fallbackValue = (item as any)[key];
    if (typeof fallbackValue === 'string' && fallbackValue.trim()) {
      return fallbackValue;
    }
  }
  return null;
};

const localizeObject = (
  item: Record<string, string> | null | undefined,
  lang: string = getCurrentLanguage()
): string | null => {
  if (!item) return null;
  return (
    item[lang] ??
    item[getCurrentLanguage()] ??
    item[FALLBACK_LANGUAGE] ??
    null
  );
};

const localizeString = (key: string): string => {
  return i18n.t(key);
};

/* ----------------------------------------
 * Public API
 * ------------------------------------- */

class LocalizerHelper {
  /* 🔹 MAIN ENTRY POINT */
  static localized(
    item: unknown,
    key?: string[],
    lang: string = getCurrentLanguage()
  ): string | null {
    if (typeof item === 'string') {
      return localizeString(item);
    }

    return localizeFirestoreItem(
      item as FirestoreLocalizedItem,
      key ?? [],
      lang
    );
  }

  /* 🔹 OBJECT LOCALIZATION */
  static localizeObject(
    item: Record<string, string>,
    lang?: string
  ) {
    return localizeObject(item, lang);
  }

  /* 🔹 LANGUAGE CONTROL */
  static getLanguage(): string {
    return getCurrentLanguage();
  }

  static async setLanguage(lang: string): Promise<void> {
    if (lang === getCurrentLanguage()) return;

    await i18n.changeLanguage(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }

  static onLanguageChanged(
    callback: (lang: string) => void
  ): () => void {
    i18n.on('languageChanged', callback);

    return () => {
      i18n.off('languageChanged', callback);
    };
  }
}

export { LocalizerHelper };
